"use client";

import { Suspense, useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  getAnalyticsBrandProperties,
  resolveAnalyticsBrandConfig,
  shouldCaptureAnalytics,
  type AnalyticsBrandConfig,
} from "@/lib/analytics-brand-config";

// Literal process.env access so Next inlines the value into the client bundle.
const TUSCAN_GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? "";
// Fallback is the live tag of Roma's standalone Ads account (2775186950), so
// the Roma host tracks correctly even when the env override is not configured.
const ROMA_GOOGLE_ADS_ID =
  process.env.NEXT_PUBLIC_ROMA_GOOGLE_ADS_ID ?? "AW-18333212236";
const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "li_fat_id",
] as const;

let activeBrandConfig: AnalyticsBrandConfig | null = null;
let lastTrackedPageLocation: string | null = null;

function getActiveBrandConfig(): AnalyticsBrandConfig | null {
  if (typeof window === "undefined") return null;
  return activeBrandConfig ?? resolveAnalyticsBrandConfig();
}

function getGoogleAdsId(brandConfig: AnalyticsBrandConfig) {
  if (brandConfig.restaurantId === "roma-kitchen") return ROMA_GOOGLE_ADS_ID;
  if (brandConfig.restaurantId === "tuscan-cove") return TUSCAN_GOOGLE_ADS_ID;
  return "";
}

function shouldSkipGoogleAnalytics() {
  const brandConfig = getActiveBrandConfig();
  if (!brandConfig) return true;
  if (!shouldCaptureAnalytics(brandConfig.hostname)) return true;
  return !brandConfig.ga4Id;
}

function sanitizeUrl(url: string) {
  if (!url) return undefined;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    return undefined;
  }
}

function getReferrerHost(referrer: string | undefined) {
  if (!referrer || referrer === "direct") return undefined;

  try {
    return new URL(referrer).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

function getGa4AttributionParams(brandConfig: AnalyticsBrandConfig) {
  if (typeof window === "undefined") return {};

  const searchParams = new URLSearchParams(window.location.search);
  const attribution: Record<string, string> = {};
  let hasUrlAttribution = false;

  ATTRIBUTION_KEYS.forEach((key) => {
    const value = searchParams.get(key)?.trim();
    if (value) {
      attribution[key] = value.slice(0, 220);
      hasUrlAttribution = true;
    }
  });

  try {
    if (hasUrlAttribution) {
      sessionStorage.setItem(
        brandConfig.storage.ga4Attribution,
        JSON.stringify(attribution),
      );
      return attribution;
    }

    const stored = sessionStorage.getItem(brandConfig.storage.ga4Attribution);
    if (!stored) return attribution;

    const parsed = JSON.parse(stored) as Record<string, unknown>;
    ATTRIBUTION_KEYS.forEach((key) => {
      const value = parsed[key];
      if (typeof value === "string" && value.trim()) {
        attribution[key] = value.trim().slice(0, 220);
      }
    });
  } catch {
    // Analytics should never depend on storage availability.
  }

  return attribution;
}

function inferTrafficSource(params: Record<string, string>, referrerHost?: string) {
  if (params.utm_source) return params.utm_source;
  if (params.gclid || params.gbraid || params.wbraid) return "google";
  if (params.fbclid) return "facebook";
  if (params.msclkid) return "bing";
  if (params.ttclid) return "tiktok";
  if (params.twclid) return "x";
  if (params.li_fat_id) return "linkedin";
  if (!referrerHost) return "direct";

  if (/google\./i.test(referrerHost)) return "google";
  if (/bing\./i.test(referrerHost)) return "bing";
  if (/yahoo\./i.test(referrerHost)) return "yahoo";
  if (/duckduckgo\./i.test(referrerHost)) return "duckduckgo";
  if (/facebook\.|instagram\.|l\.facebook/i.test(referrerHost)) return "meta";
  if (/tiktok\./i.test(referrerHost)) return "tiktok";
  if (/linkedin\./i.test(referrerHost)) return "linkedin";

  return referrerHost;
}

function inferTrafficMedium(params: Record<string, string>, referrerHost?: string) {
  if (params.utm_medium) return params.utm_medium;
  if (params.gclid || params.gbraid || params.wbraid || params.fbclid || params.msclkid) {
    return "paid";
  }
  if (!referrerHost) return "direct";
  if (/google\.|bing\.|yahoo\.|duckduckgo\./i.test(referrerHost)) return "organic";
  if (/facebook\.|instagram\.|l\.facebook|tiktok\.|linkedin\./i.test(referrerHost)) {
    return "social_referral";
  }
  return "referral";
}

function getGa4AttributionProperties(brandConfig: AnalyticsBrandConfig): Record<string, string> {
  const params = getGa4AttributionParams(brandConfig);
  const initialReferrer = document.referrer ? (sanitizeUrl(document.referrer) ?? "direct") : "direct";
  const referrerHost = getReferrerHost(initialReferrer);
  const trafficSource = inferTrafficSource(params, referrerHost);
  const trafficMedium = inferTrafficMedium(params, referrerHost);
  const properties: Record<string, string> = {
    ...params,
    ...getAnalyticsBrandProperties(brandConfig),
    traffic_source: trafficSource,
    traffic_medium: trafficMedium,
  };

  if (initialReferrer !== "direct") {
    properties.initial_referrer = initialReferrer;
  }
  if (referrerHost) {
    properties.referrer_host = referrerHost;
  }

  return properties;
}

function getPageLocation(url?: string) {
  const location = new URL(url || window.location.href, window.location.origin);
  return location.href;
}

function getPagePath(pageLocation: string) {
  const location = new URL(pageLocation);
  return `${location.pathname}${location.search}`;
}

function GoogleAnalyticsPageView({ enabled }: { enabled: boolean }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!enabled || !pathname || shouldSkipGoogleAnalytics()) return;

    const queryString = searchParams?.toString();
    trackPageView(queryString ? `${pathname}?${queryString}` : pathname);
  }, [enabled, pathname, searchParams]);

  return null;
}

export function GoogleAnalytics() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const config = resolveAnalyticsBrandConfig();
    if (!config || !shouldCaptureAnalytics(config.hostname)) return;

    // Roma has a Google Ads tag but no GA4 property, so the gtag bootstrap
    // must run whenever the brand has either id, not only when GA4 exists.
    const googleAdsId = getGoogleAdsId(config);
    if (!config.ga4Id && !googleAdsId) return;

    activeBrandConfig = config;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(command, targetOrEventName, params) {
      void command;
      void targetOrEventName;
      void params;
      // gtag's canonical snippet pushes the browser Arguments object.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    if (config.ga4Id) {
      window.gtag("config", config.ga4Id, {
        send_page_view: false,
      });
    }
    if (googleAdsId) {
      window.gtag("config", googleAdsId);
    }
    setIsReady(true);

    const loaderId = config.ga4Id || googleAdsId;
    const injectLibrary = () => {
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src="https://www.googletagmanager.com/gtag/js?id=${loaderId}"]`,
      );
      if (existingScript) return;

      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${loaderId}`;
      document.head.appendChild(script);
    };

    // Load gtag on first interaction, or 5s after window load as a fallback.
    // Events pushed before then queue in dataLayer and flush on load, so
    // nothing is dropped; the ~250KB of gtag containers (GA4 + Google Ads)
    // just stops competing with the page during the LCP-critical phase.
    let injected = false;
    let fallbackTimer: number | undefined;
    const interactionEvents = [
      "pointerdown",
      "keydown",
      "touchstart",
      "wheel",
    ] as const;

    const cleanupTriggers = () => {
      interactionEvents.forEach((event) =>
        window.removeEventListener(event, trigger),
      );
      window.removeEventListener("load", armFallback);
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
    };

    const trigger = () => {
      if (injected) return;
      injected = true;
      cleanupTriggers();
      injectLibrary();
    };

    const armFallback = () => {
      fallbackTimer = window.setTimeout(trigger, 5000);
    };

    interactionEvents.forEach((event) =>
      window.addEventListener(event, trigger, { once: true, passive: true }),
    );
    if (document.readyState === "complete") {
      armFallback();
    } else {
      window.addEventListener("load", armFallback, { once: true });
    }

    return cleanupTriggers;
  }, []);

  // Keep the server and first client render structurally identical. Hostname
  // detection is browser-only, so branching here used to render `null` on the
  // server and a Suspense boundary on branded production hosts. The effect and
  // page-view effect already enforce the analytics capture guards.
  return (
    <Suspense fallback={null}>
      <GoogleAnalyticsPageView enabled={isReady} />
    </Suspense>
  );
}

// Track page views (for client-side navigation)
export function trackPageView(url?: string) {
  const brandConfig = getActiveBrandConfig();
  if (!brandConfig?.ga4Id || shouldSkipGoogleAnalytics() || !window.gtag) return;

  const pageLocation = getPageLocation(url);
  const pageReferrer = lastTrackedPageLocation ?? sanitizeUrl(document.referrer);
  if (lastTrackedPageLocation === pageLocation) return;

  window.gtag("event", "page_view", {
    send_to: brandConfig.ga4Id,
    page_path: getPagePath(pageLocation),
    page_location: pageLocation,
    page_title: document.title,
    ...(pageReferrer ? { page_referrer: pageReferrer } : {}),
    ...getGa4AttributionProperties(brandConfig),
  });
  lastTrackedPageLocation = pageLocation;
}

// Track custom events
export function trackGAEvent(
  action: string,
  category: string,
  label?: string,
  value?: number,
) {
  if (!shouldSkipGoogleAnalytics() && window.gtag) {
    window.gtag("event", action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
}
